import { useState } from 'react';
import Modal from '@/components/ui/Modal';
import { TIPO_AUSENCIA_LABEL, type Ausencia, type DocumentoEmpleado, type TipoAusencia } from '@/lib/personal';

/**
 * Alta y edición de una ausencia.
 *
 * «Descuenta vacaciones» sale del tipo pero se puede forzar: hay convenios
 * donde algún permiso sí sale del saldo, y no merece la pena una tabla de
 * excepciones para lo que se resuelve marcando una casilla.
 *
 * Los días se cuentan aquí solo para enseñarlos mientras se elige; el número
 * bueno lo calcula el servidor al guardar.
 */
interface Props {
  abierto: boolean;
  ausencia: Ausencia | null;
  documentos: DocumentoEmpleado[];
  /** Día que se ha pulsado en la cuadrícula del equipo, si viene de ahí. */
  diaInicial?: string;
  onCerrar: () => void;
  onGuardar: (datos: Record<string, unknown>) => Promise<void>;
  onBorrar?: () => Promise<void>;
}

const TIPOS = Object.keys(TIPO_AUSENCIA_LABEL) as TipoAusencia[];
const DESCUENTAN: TipoAusencia[] = ['vacaciones'];

function diasEntre(desde: string, hasta: string): number {
  if (!desde || !hasta || hasta < desde) return 0;
  const a = Date.UTC(+desde.slice(0, 4), +desde.slice(5, 7) - 1, +desde.slice(8, 10), 12);
  const b = Date.UTC(+hasta.slice(0, 4), +hasta.slice(5, 7) - 1, +hasta.slice(8, 10), 12);
  return Math.round((b - a) / 86_400_000) + 1;
}

export default function ModalAusencia({ abierto, ausencia, documentos, diaInicial, onCerrar, onGuardar, onBorrar }: Props) {
  const [tipo, setTipo] = useState<TipoAusencia>(ausencia?.tipo || 'vacaciones');
  const [desde, setDesde] = useState(ausencia?.desde || diaInicial || '');
  const [hasta, setHasta] = useState(ausencia?.hasta || diaInicial || '');
  const [descuenta, setDescuenta] = useState(ausencia?.descuentaVacaciones ?? DESCUENTAN.includes('vacaciones'));
  const [tocadoDescuenta, setTocado] = useState(false);
  const [nota, setNota] = useState(ausencia?.nota || '');
  const [documentoId, setDocumentoId] = useState(ausencia?.documentoId || '');
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Mientras no se toque la casilla a mano, sigue al tipo.
  const cambiarTipo = (t: TipoAusencia) => {
    setTipo(t);
    if (!tocadoDescuenta) setDescuenta(DESCUENTAN.includes(t));
  };

  const dias = diasEntre(desde, hasta);

  const enviar = async (e: React.FormEvent) => {
    e.preventDefault();
    setGuardando(true);
    setError(null);
    try {
      await onGuardar({
        tipo,
        desde,
        hasta,
        descuentaVacaciones: descuenta,
        nota: nota.trim() || undefined,
        documentoId: documentoId || undefined,
      });
      onCerrar();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se ha podido guardar');
    } finally {
      setGuardando(false);
    }
  };

  const borrar = async () => {
    if (!onBorrar || !confirm('¿Eliminar esta ausencia?')) return;
    setGuardando(true);
    try {
      await onBorrar();
      onCerrar();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se ha podido borrar');
      setGuardando(false);
    }
  };

  return (
    <Modal isOpen={abierto} onClose={onCerrar} title={ausencia ? 'Editar ausencia' : 'Nueva ausencia'}>
      <form onSubmit={enviar} className="space-y-4">
        <label className="block">
          <span className="text-xs font-medium text-sereno-dark/60">Tipo</span>
          <select className="input mt-1" value={tipo} onChange={(e) => cambiarTipo(e.target.value as TipoAusencia)}>
            {TIPOS.map((t) => (
              <option key={t} value={t}>{TIPO_AUSENCIA_LABEL[t]}</option>
            ))}
          </select>
        </label>

        <div className="grid grid-cols-2 gap-4">
          <label className="block">
            <span className="text-xs font-medium text-sereno-dark/60">Desde *</span>
            <input
              type="date" required className="input mt-1" value={desde}
              onChange={(e) => { setDesde(e.target.value); if (!hasta || hasta < e.target.value) setHasta(e.target.value); }}
            />
          </label>
          <label className="block">
            <span className="text-xs font-medium text-sereno-dark/60">Hasta *</span>
            <input type="date" required min={desde} className="input mt-1" value={hasta} onChange={(e) => setHasta(e.target.value)} />
          </label>
        </div>

        {dias > 0 && (
          <p className="text-xs text-sereno-dark/50">
            {dias} día{dias === 1 ? '' : 's'} natural{dias === 1 ? '' : 'es'}, contando los dos extremos.
          </p>
        )}

        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox" checked={descuenta}
            onChange={(e) => { setDescuenta(e.target.checked); setTocado(true); }}
          />
          Descuenta del saldo de vacaciones
        </label>

        <label className="block">
          <span className="text-xs font-medium text-sereno-dark/60">Justificante</span>
          <select className="input mt-1" value={documentoId} onChange={(e) => setDocumentoId(e.target.value)}>
            <option value="">— Ninguno —</option>
            {documentos.map((d) => (
              <option key={d.id} value={d.id}>{d.fileName}</option>
            ))}
          </select>
        </label>

        <label className="block">
          <span className="text-xs font-medium text-sereno-dark/60">Nota</span>
          <textarea className="input mt-1" rows={2} value={nota} onChange={(e) => setNota(e.target.value)} />
        </label>

        {error && <p className="text-sm text-red-600">{error}</p>}

        <div className="flex justify-between items-center pt-2">
          {onBorrar ? (
            <button type="button" onClick={() => void borrar()} className="text-sm text-red-600 hover:underline">
              Eliminar
            </button>
          ) : <span />}
          <div className="flex gap-2">
            <button type="button" onClick={onCerrar} className="btn-secondary">Cancelar</button>
            <button type="submit" disabled={guardando} className="btn-primary disabled:opacity-50">
              {guardando ? 'Guardando…' : 'Guardar'}
            </button>
          </div>
        </div>
      </form>
    </Modal>
  );
}
